// ============================================================
// DATA ADAPTER — the abstraction that decouples screens from the backend.
// Screens/hooks call `getAdapter()` and never import Firestore or demo data
// directly. Today getAdapter() returns mockAdapter (everything works offline);
// later, flip DATA_BACKEND to 'firebase' to swap in firebaseAdapter with the
// SAME interface — no screen code changes.
//
// All reads return whole collections; permission filtering happens in
// src/lib/permissions.ts on the client (UI layer) AND in Firestore Security
// Rules on the server (enforced layer, P4). The adapter is permission-agnostic.
// ============================================================
import type {
  AxCase,
  AxKpi,
  AxMemberProgress,
  AxPhase,
  AxReport,
  AxSupport,
  AxWin,
  CalendarEvent,
  DriveActivity,
  DriveActivityHistoryWeek,
  DriveFile,
  Knowledge,
  KnowledgeItem,
  MeetingImport,
  MeetingNote,
  Member,
  MemberApp,
  OneOnOne,
  Project,
  Prompt,
  Task,
} from '../types/models';

/** Patch shape for task updates (single-source mutation).
 *  Google 同期メタ（googleTaskId 等）も patch 可能にして taskSync が永続化できるようにする。 */
export type TaskPatch = Partial<
  Pick<
    Task,
    | 'status'
    | 'due'
    | 'type'
    | 'title'
    | 'assignee'
    | 'googleTaskId'
    | 'googleTaskListId'
    | 'gtUpdated'
    | 'syncedAt'
    | 'updatedAt'
  >
> & {
  /** プロダクト（PJ）の付け替え。null＝未分類（本人限定）。TaskCreate と同じく null を許す。
   *  firebaseAdapter は null をそのまま Firestore に保存（stripUndefined は undefined のみ除去）。
   *  projectId を持つ＝isTagged になり Rules 上チーム共有、未分類（null/'')＝本人専用。 */
  projectId?: string | null;
};

/** Shape for creating a new task (Google→アプリ 取り込み / メンバーの自タスク追加用)。
 *  assignee/title 必須。projectId は未分類を許す（null）。残りは適切な既定で補完。 */
export type TaskCreate = {
  title: string;
  assignee: string;
  status?: Task['status'];
  due?: string;
  type?: Task['type'];
  projectId?: string | null;
  /** 明示指定時はこの値を completedAt に使う（未指定なら status==='done' で now を刻む従来動作）。
   *  Google取り込みの過去完了タスクは null を渡し、"今月の完了" サマリーの水増しを防ぐ。 */
  completedAt?: string | null;
  googleTaskId?: string;
  googleTaskListId?: string;
  gtUpdated?: string;
  syncedAt?: number;
  updatedAt?: number;
};

/** meetingImports の upsert 入力。updatedAt は adapter が Date.now() を刻む。
 *  byId は呼び出し側が viewer.id（安定 member id）を渡す（Rules: byId==本人 を強制）。 */
export type MeetingImportWrite = Omit<MeetingImport, 'updatedAt'>;

/** Shape for creating a new member（Settings のメンバー追加・admin のみ）。
 *  name/email/role/exec/projects を受け取る。doc id は adapter 側で安定 slug を採番する
 *  （email ローカル部を英数化＋衝突回避）。uid は付けない＝本人初回ログイン時に
 *  provisionMe が email 照合で uid doc を作る前提。initials は未指定なら name 先頭から補完。 */
export type MemberCreate = {
  name: string;
  email: string;
  role: Member['role'];
  exec: boolean;
  projects: string[];
  /** 未指定なら name の先頭文字で補完する。 */
  initials?: string;
};

/** Patch shape for member updates（role/exec/所属PJ のインライン編集・admin のみ）。
 *  name/email/initials も一応 patch 可能にしておく（現行UIは role/exec/projects のみ使う）。 */
export type MemberPatch = Partial<Pick<Member, 'name' | 'email' | 'role' | 'exec' | 'projects' | 'initials'>>;

/** Shape for creating a personal calendar event（メンバーの「予定を追加」）。
 *  ownerId は adapter 側で **必ず現在ユーザーの uid** を埋める（呼び出し側は渡さない）＝
 *  他人名義・PJ予定の作成をクライアントから不可能にする。projectId / meetLink は受けない
 *  （個人予定に限定・Rules の create 条件と一致）。kind は既定 'plan'。 */
export type CalendarEventCreate = {
  /** 予定の所有者 uid（＝request.auth.uid）。Rules: create は ownerId==自分 を要求。 */
  ownerId: string;
  title: string;
  date: string; // 'YYYY-MM-DD'
  /** 任意の開始時刻 'HH:MM'。未指定なら終日扱い（time 無し）。 */
  time?: string;
  /** 任意メモ。 */
  note?: string;
};

/** Shape for creating an AX case（ナレッジ画面「+ 追加」・全員投稿可）。
 *  authorId は呼び出し側が viewer.id（安定 member id）を渡す。Rules の create 条件
 *  （authorId == getMember().id）と一致しないと拒否される＝他人名義の投稿不可。 */
export type AxCaseCreate = {
  title: string;
  category: string;
  method: string;
  effect: string;
  authorId: string;
};

/** Shape for creating a prompt（ナレッジ画面「+ 追加」・全員投稿可）。 */
export type PromptCreate = {
  title: string;
  use: string;
  body: string;
  tags?: string[];
  authorId: string;
};

/** Shape for creating a knowledge item（ナレッジ画面「+ 追加」・全員投稿可・v1 は全社共通のみ）。 */
export type KnowledgeItemCreate = {
  title: string;
  body: string;
  category?: string;
  authorId: string;
};

export interface DataAdapter {
  /** Identifier for diagnostics / dev banner. */
  readonly name: 'mock' | 'firebase';

  // ── core collections ──
  getMembers(): Promise<Member[]>;
  /** 新規メンバー作成（Settings・admin のみ）。安定 slug を doc id にする（uid は付けない）。
   *  mockAdapter は in-memory push／firebaseAdapter は setDoc(members/{slug})。返り値は採番済み Member。
   *  write 可否は Rules（members write = isAdmin）が強制する（UIゲートと二層）。 */
  createMember(input: MemberCreate): Promise<Member>;
  /** メンバー更新（role/exec/所属PJ のインライン編集・admin のみ）。
   *  mockAdapter は in-memory patch／firebaseAdapter は updateDoc。返り値は patch 反映後の Member。 */
  updateMember(id: string, patch: MemberPatch): Promise<Member>;
  getProjects(): Promise<Project[]>;
  /** 議事録一覧。crossRead（admin/exec）は全読み。一般メンバーは限定公開（restricted）対応の
   *  2クエリ ∪ dedupe（①PJ公開分＝projectId in 所属PJ + restricted==false／
   *  ②自分が参加者の限定公開分＝participantIds array-contains 自分 + restricted==true）。
   *  mockAdapter も同じ意味論で scope フィルタする（scope 無しの DEV は全件）。 */
  getNotes(): Promise<MeetingNote[]>;
  /** Patch one meetingNote (人手編集＝AI議事録の手直し)。
   *  mockAdapter は内部配列を patch マージ／firebaseAdapter は updateDoc。
   *  返り値は patch 反映後の MeetingNote。tasks ドキュメントには波及しない（別物）。
   *  write 権限は Rules で admin/exec/該当PJのPM に絞る（UIゲートと二層）。 */
  updateNote(id: string, patch: Partial<MeetingNote>): Promise<MeetingNote>;
  /** The editable single source. P3 wires Calendar/MyTasks/PJ dash to this. */
  getTasks(): Promise<Task[]>;
  /** Patch one task. mockAdapter mutates in-memory; firebaseAdapter writes Firestore.
   *  Returns the updated task. completedAt is set/cleared on status transitions to/from 'done'.
   *  updatedAt は毎回 Date.now() をセットする（Google 同期の push 要否判定の基準）。 */
  updateTask(id: string, patch: TaskPatch): Promise<Task>;
  /** Create a new task（Google→アプリ取り込み / メンバーの自タスク追加）。doc id は自動採番。
   *  mockAdapter は in-memory に push／firebaseAdapter は addDoc。返り値は採番済み Task。 */
  createTask(input: TaskCreate): Promise<Task>;
  /** タスクを削除（本人の自タスク or admin）。mockAdapter は in-memory から除去／firebaseAdapter は deleteDoc。
   *  Rules（delete = admin || 本人担当）が可否を強制する。Google側の連携タスク削除は呼び出し側の責務（best effort）。 */
  deleteTask(id: string): Promise<void>;

  // ── realtime subscriptions（改善④）──
  /** tasks のリアルタイム購読。**getTasks と同一のスコープドクエリ**の結果を、変更のたびに cb へ渡す。
   *  戻り値は購読解除関数（画面 unmount で必ず呼ぶ）。
   *  - firebaseAdapter: onSnapshot で購読。購読確立・受信のエラーは throw せず console.warn のみ
   *    （＝画面は既存の get 結果で動き続ける）。
   *  - mockAdapter: 現在の配列を即時1回 cb に渡すだけ（no-op unsubscribe を返す・DEV挙動は実質不変）。 */
  subscribeTasks(cb: (tasks: Task[]) => void): () => void;
  /** meetingNotes のリアルタイム購読。**getNotes と同一のスコープドクエリ**。作法は subscribeTasks と同じ。 */
  subscribeNotes(cb: (notes: MeetingNote[]) => void): () => void;

  // ── meetingImports（Meet議事録取り込みの共有ステータス・doc id = calendar eventId）──
  /** 共有ステータス一覧（imported/dismissed/pending）。登録メンバー全員 read 可（Rules）。 */
  getMeetingImports(): Promise<MeetingImport[]>;
  /** 共有ステータスの upsert（setDoc 全量書き込み）。doc id = record.eventId。
   *  「取得しない」（dismissed）・取得成功（imported+noteId）・復元（pending）の全遷移がこの1本。
   *  write 可否は Rules（isRegistered ＋ status 検証 ＋ byId==本人）が強制する。返り値は保存済みレコード。 */
  setMeetingImport(record: MeetingImportWrite): Promise<MeetingImport>;
  /** meetingImports のリアルタイム購読（NotesList の取り込みセクション用）。
   *  誰かが取得/非表示にしたら全員の一覧から消える。作法は subscribeNotes と同じ
   *  （エラーは console.warn・mock は即時1回＋書き込みのたび再emit）。 */
  subscribeMeetingImports(cb: (records: MeetingImport[]) => void): () => void;

  // ── info collections ──
  getKnowledge(): Promise<Knowledge[]>;
  getDriveFiles(): Promise<DriveFile[]>;
  getCalendarEvents(): Promise<CalendarEvent[]>;
  /** 個人予定を作成（メンバーの「予定を追加」）。doc id は自動採番。
   *  mockAdapter は in-memory に push／firebaseAdapter は addDoc。返り値は採番済み CalendarEvent。
   *  ownerId は input で受けた uid をそのまま保存する（呼び出し側＝Calendar が viewer の uid を渡す）。
   *  Rules（create = 本人の個人予定のみ）が書き込み可否を強制する（UIゲートと二層）。 */
  createCalendarEvent(input: CalendarEventCreate): Promise<CalendarEvent>;
  /** 個人予定を削除（本人が作った予定のみ）。mockAdapter は in-memory から除去／firebaseAdapter は deleteDoc。
   *  Rules（delete = 本人の個人予定 or admin）が可否を強制する。 */
  deleteCalendarEvent(id: string): Promise<void>;
  getAxCases(): Promise<AxCase[]>;
  getPrompts(): Promise<Prompt[]>;
  getMemberApps(): Promise<MemberApp[]>;
  /** メンバー投稿（ナレッジ画面「+ 追加」）。write 可否は Rules（authorId==本人）が強制する。 */
  createAxCase(input: AxCaseCreate): Promise<AxCase>;
  createPrompt(input: PromptCreate): Promise<Prompt>;
  getKnowledgeItems(): Promise<KnowledgeItem[]>;
  createKnowledgeItem(input: KnowledgeItemCreate): Promise<KnowledgeItem>;

  // ── axReports（AX入力・全員が使える月次自己申告） ──
  /** 自分の今月（または指定月）のレポートを取得。未提出なら null。
   *  doc id 規則: `${memberId}_${month}`。read 可否は Rules（本人 or exec）が強制する。 */
  getMyAxReport(memberId: string, month: string): Promise<AxReport | null>;
  /** 自分のレポートを upsert（新規作成 or 更新）。write 可否は Rules（memberId==本人）が強制する。 */
  saveAxReport(report: AxReport): Promise<void>;
  /** 指定月の全員分のレポート（AxExec の実データ集計に使用）。read は exec のみ（Rules）。 */
  getAxReports(month: string): Promise<AxReport[]>;

  // ── exec-only collections (read enforced exec-only at Rules layer in P4) ──
  getOneOnOne(): Promise<OneOnOne[]>;
  getAxProgress(): Promise<{
    memberProgress: AxMemberProgress[];
    kpi: AxKpi;
    phases: AxPhase[];
    support: AxSupport[];
    wins: AxWin[];
  }>;
  /** 作業ログ（Drive Activity）。外部 GitHub Action が driveActivity/latest に書き込む単一バンドル。
   *  まだ集計が一度も走っていない（doc 未生成）場合は null を返す（getAxProgress と異なり throw しない。
   *  画面側で「まだ集計がありません」の空状態を出すため）。 */
  getDriveActivity(): Promise<DriveActivity | null>;
  /** 直近 limit 週の活動量推移（driveActivityHistory）を week 降順で返す。
   *  集計スクリプト（temp_activity_digest.py）が週次で軽量サマリのみ書き込む。
   *  データが無ければ空配列（画面側は「まだ推移データがありません」を出す）。read は exec のみ（Rules）。 */
  getDriveActivityHistory(limit?: number): Promise<DriveActivityHistoryWeek[]>;
}

// ── viewer scope (P4) ─────────────────────────────────────
// 非admin/非exec のメンバーは tasks/meetingNotes の「コレクション全読み」を
// Security Rules が拒否する（list は返る全docが rule を満たす必要があり、他人の
// タスク/他PJの議事録が混ざると query 全体が弾かれる）。そこで firebaseAdapter は
// viewer に応じて絞り込み query を投げる。scope は AuthContext が viewer 確定時に
// 1度だけセットする単一ソース（全 getTasks/getNotes 呼び出し箇所を一律でカバー＝
// 呼び出し側の取りこぼしを構造的に防ぐ）。mockAdapter はこれを無視する（Rules が
// 無いので全件返し、client 側 permissions.ts で絞る）。
export interface ViewerScope {
  /** viewer.id（安定 member id）。tasks.assignee と一致し、Rules の getMember().id とも一致する。 */
  memberId: string;
  /** viewer.projects。meetingNotes.projectId と一致し、Rules の inProjects と対応。 */
  projects: string[];
  /** admin || exec のとき true＝コレクション全読みを許可（横断画面のため）。 */
  crossRead: boolean;
}
let _scope: ViewerScope | null = null;
/** viewer 確定時に AuthContext が呼ぶ。ログアウト時は null でクリア。 */
export function setActiveScope(scope: ViewerScope | null): void {
  _scope = scope;
}
/** firebaseAdapter が絞り込み query の要否を判断するために読む。 */
export function getActiveScope(): ViewerScope | null {
  return _scope;
}

// ── backend selection ─────────────────────────────────────
const ENV_BACKEND = import.meta.env.VITE_DATA_BACKEND;
export const DATA_BACKEND: 'mock' | 'firebase' = ENV_BACKEND === 'firebase' ? 'firebase' : 'mock';

let _adapter: DataAdapter | null = null;

/**
 * Returns the active data adapter (singleton). Lazy dynamic-import keeps the
 * Firebase SDK out of the bundle until/unless the firebase backend is selected.
 */
export async function getAdapter(): Promise<DataAdapter> {
  if (_adapter) return _adapter;
  if (DATA_BACKEND === 'firebase') {
    const { firebaseAdapter } = await import('./firebaseAdapter');
    _adapter = firebaseAdapter;
  } else {
    const { mockAdapter } = await import('./mockAdapter');
    _adapter = mockAdapter;
  }
  return _adapter;
}
